import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, getDoc, doc, Timestamp } from 'firebase/firestore';
import { db as firebaseDb } from '../firebase';
import { Clock, MapPin, Calendar } from 'lucide-react';

const isSameDay = (d1, d2) =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

const getStatusStyle = (status) => {
  switch (status) {
    case 'assigne':    return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700';
    case 'completed':  return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700';
    case 'in_progress':return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700';
    case 'reserve':    return 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700';
    default:           return 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-600';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'assigne':     return 'Assigné';
    case 'completed':   return 'Terminé';
    case 'in_progress': return 'En cours';
    case 'reserve':     return 'Réservé';
    case 'libre':       return 'Libre';
    default:            return status || '—';
  }
};

const getDotColor = (status, isCurrent, isPast) => {
  if (isCurrent) return 'bg-amber-500 animate-pulse';
  if (isPast)    return 'bg-gray-300 dark:bg-gray-600';
  switch (status) {
    case 'completed':   return 'bg-green-500';
    case 'in_progress': return 'bg-amber-500';
    default:            return 'bg-blue-500';
  }
};

const EmployeeDayView = ({ db = firebaseDb, employee, currentDate, hasPermission }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAppointments = useCallback(async () => {
    if (!db || !employee?.id) return;
    setLoading(true);
    try {
      const startOfDay = new Date(currentDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(currentDate);
      endOfDay.setHours(23, 59, 59, 999);

      const q = query(
        collection(db, 'appointments'),
        where('employeeId', '==', employee.id),
        where('date', '>=', Timestamp.fromDate(startOfDay)),
        where('date', '<=', Timestamp.fromDate(endOfDay))
      );

      const snap = await getDocs(q);
      const raw = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Enrichir avec les données client (nom + couleur)
      const clientCache = new Map();
      const fetched = await Promise.all(raw.map(async apt => {
        if (!apt.clientId) return apt;
        if (!clientCache.has(apt.clientId)) {
          const clientSnap = await getDoc(doc(db, 'clients', apt.clientId));
          clientCache.set(apt.clientId, clientSnap.exists() ? clientSnap.data() : null);
        }
        const clientData = clientCache.get(apt.clientId);
        return {
          ...apt,
          clientName: clientData ? `${clientData.lastName || ''} ${clientData.firstName || ''}`.trim() : 'Bénéficiaire inconnu',
          clientColor: clientData?.color || '#3b82f6',
        };
      }));

      fetched.sort((a, b) => {
        const [ah, am] = (a.startTime || '00:00').split(':').map(Number);
        const [bh, bm] = (b.startTime || '00:00').split(':').map(Number);
        return (ah * 60 + am) - (bh * 60 + bm);
      });
      setAppointments(fetched);
    } catch (e) {
      console.error('EmployeeDayView: fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [db, employee?.id, currentDate]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500 p-8">
        <Calendar size={52} className="mb-4 opacity-30" />
        <p className="text-lg font-medium text-gray-600 dark:text-gray-400">Aucune intervention ce jour</p>
        <p className="text-sm mt-1 text-center">
          {currentDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>
    );
  }

  // Total duration
  const totalMin = appointments.reduce((acc, apt) => {
    const [sh, sm] = (apt.startTime || '0:0').split(':').map(Number);
    const [eh, em] = (apt.endTime || '0:0').split(':').map(Number);
    return acc + ((eh * 60 + em) - (sh * 60 + sm));
  }, 0);
  const totalLabel = `${Math.floor(totalMin / 60)}h${totalMin % 60 > 0 ? String(totalMin % 60).padStart(2, '0') : ''}`;

  const now = new Date();

  return (
    <div className="h-full overflow-y-auto p-4 md:p-6">
      <div className="max-w-2xl mx-auto space-y-2">

        {/* Summary bar */}
        <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-200 dark:border-gray-700 mb-4 text-sm">
          <span className="text-gray-600 dark:text-gray-400">
            <strong className="text-gray-900 dark:text-gray-100">{appointments.length}</strong> intervention{appointments.length > 1 ? 's' : ''}
          </span>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <span className="text-gray-600 dark:text-gray-400">
            <strong className="text-gray-900 dark:text-gray-100">{totalLabel}</strong> au total
          </span>
        </div>

        {/* Timeline */}
        {appointments.map((apt, index) => {
          const aptDate = apt.date?.toDate ? apt.date.toDate() : new Date(apt.date);
          const [sh, sm] = (apt.startTime || '0:0').split(':').map(Number);
          const [eh, em] = (apt.endTime || '0:0').split(':').map(Number);
          const start = new Date(aptDate); start.setHours(sh, sm, 0, 0);
          const end = new Date(aptDate); end.setHours(eh, em, 0, 0);
          const isCurrent = start <= now && end >= now;
          const isPast = end < now && isSameDay(aptDate, now);
          const durationMin = (eh * 60 + em) - (sh * 60 + sm);
          const clientColor = apt.clientColor || '#3b82f6';

          return (
            <div key={apt.id} className="flex gap-3">
              {/* Time column */}
              <div className="w-14 text-right flex-shrink-0 pt-4">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{apt.startTime}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">{apt.endTime}</p>
              </div>

              {/* Timeline line + dot */}
              <div className="flex flex-col items-center pt-4">
                <div
                  className={`w-3 h-3 rounded-full flex-shrink-0 ${getDotColor(apt.status, isCurrent, isPast)}`}
                  style={!isCurrent && !isPast ? { backgroundColor: clientColor } : {}}
                />
                {index < appointments.length - 1 && (
                  <div className="w-0.5 flex-1 bg-gray-200 dark:bg-gray-700 mt-1 mb-1 min-h-[16px]" />
                )}
              </div>

              {/* Card */}
              <div
                className={`flex-1 mb-2 rounded-xl border p-4 transition-shadow hover:shadow-md ${getStatusStyle(apt.status)} ${isCurrent ? 'ring-2 ring-amber-400/60' : ''}`}
                style={{ borderLeftColor: clientColor, borderLeftWidth: '3px' }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-base truncate">
                      {apt.clientName || 'Bénéficiaire inconnu'}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-1 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Clock size={11} />
                        {apt.startTime} – {apt.endTime}
                      </span>
                      <span className="text-gray-300 dark:text-gray-600">·</span>
                      <span>{durationMin} min</span>
                    </div>
                  </div>

                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                    isCurrent
                      ? 'bg-amber-500 text-white'
                      : apt.status === 'completed'
                      ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                      : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                  }`}>
                    {isCurrent ? 'En cours' : getStatusLabel(apt.status)}
                  </span>
                </div>

                {/* Address */}
                {(apt.clientAddress || apt.clientCity) && (
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-2">
                    <MapPin size={11} />
                    <span className="truncate">{apt.clientAddress || apt.clientCity}</span>
                  </div>
                )}

                {/* Notes */}
                {apt.notes?.content && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 italic line-clamp-2">
                    "{apt.notes.content}"
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EmployeeDayView;
